import { useEffect, useState } from "react";
import { Check, CreditCard, Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { api, errMsg } from "@/lib/api";
import { PageHeader } from "@/components/Bits";
import { Switch } from "@/components/ui/switch";
import { useAuth } from "@/context/AuthContext";
import { MiniPlanOffer } from "@/components/MiniPlan";
import { FoundingBadge } from "@/components/FoundingBadge";
import { UpgradeConfirm } from "@/components/ProNudge";
import { BRAND_DOC_BLURB } from "@/pages/member/BrandFoundations";
import { ExitQuestion } from "@/components/ExitQuestion";

const ExitCard = () => {
  const [done, setDone] = useState(false);
  return (
    <div className="mt-8 rounded-2xl border border-border bg-card p-6" data-testid="membership-exit-card">
      {done ? <p className="text-sm text-navy" data-testid="membership-exit-thanks">Thank you. That really helps me make the club better.</p> : <>
        <h2 className="font-serif uppercase text-xl text-navy">Not joining right now?</h2>
        <p className="text-sm text-muted-foreground mt-1 mb-4">One quick question: what stopped you? Lindsay reads every answer.</p>
        <ExitQuestion onSubmit={(b) => api.post("/me/trial-exit", b)} onDone={() => setDone(true)} />
      </>}
    </div>
  );
};

export const COMMITMENT = "2-month minimum because of the onboarding work at the start. Billed monthly, and you can cancel any time from day 60.";

const PERKS = [
  "Full access to the knowledge bank, templates and the AI chat assistant",
  "A monthly question allowance, tailored to your business",
  "Templates and seasonal ideas for your side of the trade",
  "Your Roots Growth Scorecard and Roots profile",
];
const PRO_PERKS = ["Everything in Base, with unlimited questions (no monthly cap)", "Fortnightly group call with Lindsay: questions, live advice, recordings saved in the app", "Brand Foundations Document generated at onboarding"];

const NewsletterToggle = () => {
  const { user, setUser } = useAuth();
  const toggle = async (v) => {
    try {
      await api.put("/me/newsletter", { opt_in: v });
      setUser({ ...user, newsletter_opt_in: v });
      toast.success(v ? "You're on Lindsay's newsletter list" : "You've been taken off the newsletter list");
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <label className="mt-8 flex items-center gap-3 text-sm border-t border-border pt-6">
      <Switch checked={!!user?.newsletter_opt_in} onCheckedChange={toggle} data-testid="membership-newsletter-switch" />
      Send me Lindsay's newsletter with marketing tips and event news
    </label>
  );
};

export default function Membership() {
  const [access, setAccess] = useState(null);
  const [busy, setBusy] = useState(false);
  const [orders, setOrders] = useState([]);
  const [upOpen, setUpOpen] = useState(false);
  useEffect(() => {
    api.get("/me/access").then((r) => setAccess(r.data)).catch(() => {});
    api.get("/me/plan-orders").then((r) => setOrders(r.data)).catch(() => {});
  }, []);

  const subscribe = async (item) => {
    setBusy(true);
    try {
      const { data } = await api.post("/payments/checkout", { origin_url: window.location.origin, item });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(errMsg(e)); setBusy(false); }
  };
  const manage = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/payments/portal", { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) { toast.error(errMsg(e)); setBusy(false); }
  };

  if (!access) return null;
  const subscribed = ["active", "trialing"].includes(access.subscription_status);
  const trialDays = access.in_trial ? Math.max(0, Math.ceil((new Date(access.trial_ends_at) - new Date()) / 86400000)) : 0;
  const tierCard = (item, name, price, perks, dark) => (
    <div className={`rounded-3xl p-7 ${dark ? "bg-navy text-white" : "border border-border bg-card"}`} data-testid={`membership-tier-${item}`}>
      <div className={`text-xs font-mono uppercase tracking-[0.2em] ${dark ? "text-sun" : "text-pinkb"}`}>{name}</div>
      <div className="font-serif text-5xl mt-2" data-testid={`membership-price-${item}`}>{price}<span className="text-lg opacity-70">/month</span></div>
      <ul className="mt-5 space-y-2">{perks.map((p) => <li key={p} className="flex gap-2 text-sm"><Check className={`h-4 w-4 mt-0.5 shrink-0 ${dark ? "text-sun" : "text-leaf"}`} />{p}</li>)}</ul>
      {dark && <p className="mt-4 rounded-xl bg-white/10 p-3 text-xs text-white/90" data-testid="pro-brand-doc-callout"><span className="font-medium text-sun">Brand Foundations Document: </span>{BRAND_DOC_BLURB}</p>}
      {!subscribed && <p className={`mt-4 text-xs ${dark ? "text-white/70" : "text-muted-foreground"}`} data-testid={`membership-commitment-${item}`}>{COMMITMENT}</p>}
      {item === "pro" && subscribed && access.tier === "club" && <Button onClick={() => setUpOpen(true)} data-testid="membership-upgrade-pro" className="mt-6 rounded-full bg-pinkb hover:bg-pinkb/90 text-white">Upgrade to Pro</Button>}
      {access.tier === item && subscribed ? <p className="mt-6 text-sm font-medium" data-testid={`membership-current-${item}`}>Your current plan</p>
        : !access.beta_mode && !subscribed && <Button onClick={() => subscribe(item)} disabled={busy} data-testid={`membership-subscribe-${item}`} className={`mt-6 rounded-full ${dark ? "bg-pinkb hover:bg-pinkb/90" : "bg-navy hover:bg-navy/90"} text-white`}>{busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Choose {name}</Button>}
    </div>
  );
  return (
    <div className="max-w-4xl">
      <PageHeader eyebrow="Membership" title={subscribed ? "You're a member." : access.beta_mode ? "Free while we test." : access.in_trial ? `${trialDays} days left of your free trial.` : "Choose your membership."}>
        <FoundingBadge testId="membership-founding-badge" />
      </PageHeader>
      {access.beta_mode && <p className="mb-6 rounded-xl bg-leaf/10 border border-leaf/30 p-4 text-sm" data-testid="membership-testing-note">You're in the testing phase, so there's nothing to pay and you have full Pro access. Lindsay will let you know before memberships start.</p>}
      {!access.beta_mode && access.free_tier && <p className="mb-6 rounded-xl bg-leaf/10 border border-leaf/30 p-4 text-sm" data-testid="membership-free-note">You have free {access.free_tier === "pro" ? "Pro" : "Base"} access, courtesy of Lindsay{access.free_until ? ` until ${new Date(access.free_until).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}` : ""}. There's nothing to pay.</p>}
      {!access.has_access && <p className="mb-6 rounded-xl border border-pinkb/40 bg-pinkb/5 p-4 text-sm" data-testid="membership-locked-note">{access.trial_days > 0 ? "Your free trial has ended. Choose a membership to keep your answers, templates and seasonal ideas coming." : "Choose a membership to unlock your answers, templates and seasonal ideas."}</p>}
      <div className="grid gap-5 md:grid-cols-2">
        {tierCard("club", "The Roots Club Base", "£49", PERKS, false)}
        {tierCard("pro", "The Roots Club Pro", "£79", PRO_PERKS, true)}
      </div>
      {subscribed && access.commitment_ends_at && new Date(access.commitment_ends_at) > new Date() && <p className="mt-6 text-sm rounded-xl bg-secondary p-4" data-testid="membership-commitment-lock">Your 2-month minimum runs until {new Date(access.commitment_ends_at).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}. You can update your card and see invoices now, and cancel from that date.</p>}
      {subscribed && <Button onClick={manage} disabled={busy} variant="outline" className="mt-6 rounded-full" data-testid="membership-manage-btn"><CreditCard className="h-4 w-4 mr-2" />Manage or change plan</Button>}
      {!subscribed && !access.has_access && access.tester_code && !access.beta_mode && (
        <div className="mt-6 rounded-2xl bg-navy text-white p-5 flex items-start gap-3 text-sm" data-testid="tester-code-box"><Sparkles className="h-5 w-5 text-sun shrink-0" />
          <span>Thank you for testing the club. Your personal code <span className="font-mono bg-white text-navy px-2 py-0.5 rounded" data-testid="tester-code">{access.tester_code.code}</span> gives you 20% off for life. It works once, just for you, until <strong data-testid="tester-code-deadline">{new Date(access.tester_code.expires_at).toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long" })}</strong>.</span></div>
      )}
      {!subscribed && !access.tester_code && access.founding_code && !access.beta_mode && (
        <div className="mt-6 rounded-2xl bg-secondary p-5 flex items-start gap-3 text-sm" data-testid="founding-code-box"><Sparkles className="h-5 w-5 text-pinkb shrink-0" />
          <span>{access.trial_link ? "As one of the first to join, use code" : "Thank you for testing the club. Use code"} <span className="font-mono bg-white px-2 py-0.5 rounded" data-testid="founding-code">{access.founding_code}</span> at checkout for 20% off, for as long as you stay a member.{access.founding_deadline && <> Claim it by <strong data-testid="founding-deadline">{new Date(access.founding_deadline).toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long" })}</strong>.</>}</span></div>
      )}
      {!subscribed && access.trial_link && !access.has_access && !access.trial_exit_answered && <ExitCard />}
      <div className="mt-6"><NewsletterToggle /></div>
      <MiniPlanOffer orders={orders} price={access.mini_plan_price} />
      <UpgradeConfirm open={upOpen} setOpen={setUpOpen} />
    </div>
  );
}
